/**
 * Date utility functions
 * 
 * This module provides functions for handling dates consistently in the application.
 * All dates are stored in UTC unless explicitly specified otherwise.
 * Display of dates should convert UTC to the user's preferred timezone.
 */

/**
 * We'll use the browser's local timezone for display when available
 */
export function getUserTimezone(): string {
    // Use the browser's timezone if available, otherwise fallback to UTC
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    }
    return 'UTC'
}

/**
 * Gets the current date in Eastern Time, normalized to UTC midnight for that date
 * This ensures that after 8pm ET (midnight UTC), we still use the current US date
 * @returns Date object representing today's date in Eastern Time, stored as UTC midnight
 */
export function getCurrentDate(): Date {
    let overrideDate: string | null = null

    // Check both process.env and localStorage for override date
    if (typeof window !== 'undefined') {
        overrideDate = window.localStorage.getItem('NEXT_PUBLIC_OVERRIDE_DATE')
    } else {
        // Convert undefined to null for consistency
        overrideDate = process.env.NEXT_PUBLIC_OVERRIDE_DATE || null
    }

    // If override date exists, use it, otherwise use current date
    if (overrideDate) {
        const parsedDate = new Date(overrideDate)
        if (!isNaN(parsedDate.getTime())) {
            // For override dates, get the midnight in Eastern timezone for that date
            return getMidnightInTimezone(parsedDate, 'America/New_York')
        } else {
            console.warn('Invalid override date format, using current date')
        }
    }

    // Get current date in Eastern Time and normalize to midnight UTC for that date
    const now = new Date()
    return getMidnightInTimezone(now, 'America/New_York')
}

/**
 * Sets the current date override value
 * @param date Date to use as override
 */
export function setCurrentDate(date: Date) {
    if (!date || isNaN(date.getTime())) {
        throw new Error('Invalid date provided to setCurrentDate')
    }
    process.env.NEXT_PUBLIC_OVERRIDE_DATE = date.toISOString()

    // If in browser, also store in localStorage
    if (typeof window !== 'undefined') {
        window.localStorage.setItem('NEXT_PUBLIC_OVERRIDE_DATE', date.toISOString())
    }
}

/**
 * Parses a date string to a Date object
 * @param dateStr Date string in ISO format or YYYY-MM-DD format
 * @returns Date object in UTC
 */
export function parseDate(dateStr: string): Date {
    // Handle simple date format (YYYY-MM-DD)
    if (dateStr.length === 10 && dateStr.includes('-')) {
        const [year, month, day] = dateStr.split('-').map(Number)
        return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0))
    }

    // Handle ISO format
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) {
        throw new Error(`Invalid date format: ${dateStr}`)
    }

    return date
}

/**
 * Normalizes a date to midnight UTC
 * @param date Date to normalize
 * @returns Date at midnight UTC on the same day
 */
export function normalizeToMidnightUTC(date: Date): Date {
    return new Date(Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        0, 0, 0, 0
    ))
}

/**
 * Formats a date for display in the user's local timezone
 * @param date Date to format (in UTC)
 * @param format Format string pattern
 * @param timezone Optional specific timezone, defaults to user's local timezone
 * @returns Formatted date string in user's timezone
 */
export function formatDateForDisplay(
    date: Date,
    format: 'short' | 'medium' | 'long' | 'full' = 'medium',
    timezone?: string
): string {
    // Use the user's local timezone by default
    const userTimezone = timezone || getUserTimezone()

    // When we receive a UTC date, we want to preserve those date components
    // even when displaying in local time. For example, if we receive April 4th UTC,
    // we want to show April 4th local time, not April 3rd.
    const localDate = new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        12, // Use noon to avoid any daylight saving time issues
        0,
        0,
        0
    )

    // Format the date directly in the target timezone
    const options: Intl.DateTimeFormatOptions = {
        timeZone: userTimezone,
        dateStyle: format
    }

    return new Intl.DateTimeFormat('en-US', options).format(localDate)
}

/**
 * Formats date and time for display in the user's local timezone
 * @param date Date to format (in UTC)
 * @param format Format string pattern
 * @param timezone Optional specific timezone, defaults to user's local timezone
 * @returns Formatted date and time string in user's timezone
 */
export function formatDateTimeForDisplay(
    date: Date,
    format: 'short' | 'medium' | 'long' | 'full' = 'medium',
    timezone?: string
): string {
    // Use the user's local timezone by default
    const userTimezone = timezone || getUserTimezone()

    const options: Intl.DateTimeFormatOptions = {
        timeZone: userTimezone,
        dateStyle: format,
        timeStyle: format
    }

    return new Intl.DateTimeFormat('en-US', options).format(date)
}

/**
 * Converts a date from UTC to the components (year, month, day) in a specific timezone
 * @param date Date object in UTC
 * @param timezone Target timezone, defaults to user's local timezone
 * @returns Object with date components in the specified timezone
 */
export function getDateComponentsInTimezone(
    date: Date,
    timezone?: string
): { year: number, month: number, day: number } {
    // Use the user's local timezone by default
    const userTimezone = timezone || getUserTimezone()

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    })

    const parts = formatter.formatToParts(date)

    return {
        year: parseInt(parts.find(part => part.type === 'year')?.value || '0'),
        month: parseInt(parts.find(part => part.type === 'month')?.value || '0'),
        day: parseInt(parts.find(part => part.type === 'day')?.value || '0')
    }
}

/**
 * Gets a date object representing midnight in the user's timezone, stored in UTC
 * @param date Date for which to get the midnight value
 * @param timezone User's timezone, defaults to user's local timezone
 * @returns Date object representing midnight in user's timezone, stored in UTC
 */
export function getMidnightInTimezone(
    date: Date,
    timezone?: string
): Date {
    // Use the user's local timezone by default
    const userTimezone = timezone || getUserTimezone()

    // Format the date in the desired timezone to get the date components
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    })

    const parts = formatter.formatToParts(date)

    const month = parseInt(parts.find(part => part.type === 'month')?.value || '1')
    const day = parseInt(parts.find(part => part.type === 'day')?.value || '1')
    const year = parseInt(parts.find(part => part.type === 'year')?.value || '2025')

    // Create a UTC date at midnight for this specific date in the timezone
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0))
}

/**
 * Converts a date string to midnight UTC
 * @param dateStr Date string in ISO format or YYYY-MM-DD format
 * @returns Date object at midnight UTC
 */
export function toMidnightUTC(dateStr: string): Date {
    const date = parseDate(dateStr)
    return new Date(Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        0, 0, 0, 0
    ))
}

/**
 * DEPRECATED: Use parseDate or toMidnightUTC instead.
 * This function will be removed in a future version.
 * 
 * For compatibility with existing code that expects Eastern Time.
 * @deprecated Use parseDate, toMidnightUTC, or other timezone-aware functions instead
 * @param dateStr Date string to convert
 * @returns Date at midnight UTC for the given date
 */
export function convertToEasternTime(dateStr: string): Date {
    // Parse the input date (keeping it in the provided time zone)
    const date = parseDate(dateStr)

    // Get this date at midnight in Eastern timezone
    return getMidnightInTimezone(date, 'America/New_York')
}

/**
 * Returns the UTC timestamps for midnight-to-midnight Eastern Time on the given date.
 * MLB games are scheduled on US Eastern Time dates, so this is needed when querying
 * games by date to avoid catching previous-night late games in the UTC window.
 * @param date A UTC midnight date representing an ET calendar date (e.g., from getCurrentDate())
 * @returns { start, end } as UTC Date objects representing midnight ET boundaries
 */
export function getETDayBoundsUTC(date: Date): { start: Date; end: Date } {
  const dateStr = date.toISOString().split('T')[0]
  const noonUTC = new Date(dateStr + 'T12:00:00Z')
  const etHour = parseInt(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      hour12: false,
    }).format(noonUTC)
  )
  const etOffsetHours = 12 - etHour
  const start = new Date(date.getTime() + etOffsetHours * 60 * 60 * 1000)
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000)
  return { start, end }
}

/**
 * Formats a Date object into a YYYY-MM-DD string in UTC.
 * @param date The Date object to format.
 * @returns A string in YYYY-MM-DD format.
 */
export function getDateString(date: Date): string {
    const year = date.getUTCFullYear()
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0') // Months are 0-indexed
    const day = date.getUTCDate().toString().padStart(2, '0')
    return `${year}-${month}-${day}`
}